import { useEffect, useRef } from "react";

import { useDispatch } from "react-redux";

import { getOrgInfoAction } from "@/store/ducks/organization";

import { AppError } from "@/types";

import { AppDispatch } from "@/store";

type MutationResult<TValue> = {
  mutateAsync: (value: TValue) => Promise<unknown>;
  isPending: boolean;
  isSuccess: boolean;
  error: AppError | null;
};

type UseSettingsToggleParams<TCurrent, TValue> = {
  currentValue: TCurrent;
  useUpdateMutation: () => MutationResult<TValue>;
  getNextValue: (currentValue: TCurrent) => TValue;
  isChecked?: (currentValue: TCurrent) => boolean;
  onSuccess?: () => void;
};

export const useSettingsToggle = <TCurrent, TValue>({
  currentValue,
  useUpdateMutation,
  getNextValue,
  isChecked,
  onSuccess,
}: UseSettingsToggleParams<TCurrent, TValue>) => {
  const dispatch: AppDispatch = useDispatch();
  const { mutateAsync, isPending, isSuccess, error } = useUpdateMutation();

  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(getOrgInfoAction());
      onSuccessRef.current?.();
    }
  }, [dispatch, isSuccess]);

  const handleToggleChange = () => {
    mutateAsync(getNextValue(currentValue));
  };

  return {
    isChecked: isChecked ? isChecked(currentValue) : Boolean(currentValue),
    isPending,
    error,
    handleToggleChange,
    mutateAsync,
  };
};
